import React, { useRef, useState } from 'react';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  FormGroup,
  Card,
  CardBody,
  Button,
  Label,
  NavLink,
  CustomInput,
  Alert,
  UncontrolledTooltip,
} from 'reactstrap';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { toast } from 'react-toastify';

import Input from '~/components/Inputs/Input';
import InputSelectAsync from '~/components/Inputs/InputSelectAsync';
import PageHeader from '~/components/Common/PageHeader';
import { defineAlignment } from '~/util/newLayoutHelpers';
import { LG } from '~/util/breakpointWidths';
import api from '~/services/api';
import history from '~/services/history';
import SecretRevealModal from './SecretRevealModal';

const AVAILABLE_SCOPES = [
  { value: 'carts:read', label: 'Carrinhos — leitura' },
  {
    value: 'carts:write',
    label: 'Carrinhos — escrita (criar/atualizar/cancelar)',
  },
  { value: 'products:read', label: 'Produtos — leitura' },
  { value: 'stores:read', label: 'Lojas — leitura' },
  { value: 'clients:read', label: 'Clientes — leitura' },
  { value: 'clients:write', label: 'Clientes — escrita' },
  { value: 'webhooks:read', label: 'Webhooks — leitura' },
  {
    value: 'webhooks:write',
    label: 'Webhooks — escrita (criar/editar/deletar)',
  },
];

const NewPartnerApplication = () => {
  const formRef = useRef(null);
  const windowSize = useSelector(({ layout }) => layout.windowSize);

  const [environment, setEnvironment] = useState('test');
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [scopesError, setScopesError] = useState(null);

  const [allStores, setAllStores] = useState(true);
  const [showStorePicker, setShowStorePicker] = useState(false);
  const [storesSearched, setStoresSearched] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);

  const [allowedIpsText, setAllowedIpsText] = useState('');

  const [revealOpen, setRevealOpen] = useState(false);
  const [createdData, setCreatedData] = useState(null);

  const toggleScope = scope => {
    setScopesError(null);
    setSelectedScopes(current => {
      if (current.includes(scope)) {
        return current.filter(s => s !== scope);
      }
      return [...current, scope];
    });
  };

  const loadStoreOptions = async (inputValue, cb) => {
    if (inputValue.length < 2) return;
    try {
      const { data } = await api.get('/stores', {
        params: { query: inputValue },
      });

      if (!data?.stores?.length) {
        return;
      }

      const filtered = data.stores.filter(
        s => !selectedStores.some(store => store.value === s.id)
      );

      const options = filtered.map(s => ({
        value: s.id,
        label: s.fantasy_name,
      }));

      setStoresSearched(prev => [...prev, ...data.stores]);
      cb(options);
    } catch (err) {
      // silencia
    }
  };

  const addStore = option => {
    if (!option) return;
    const store = storesSearched.find(p => p.id === option.value);
    if (!store) return;
    setSelectedStores([
      ...selectedStores,
      { value: store.id, label: store.fantasy_name },
    ]);
    setShowStorePicker(false);
  };

  const removeStore = option => {
    setSelectedStores(selectedStores.filter(s => s.value !== option.value));
  };

  const parseAllowedIps = () => {
    return allowedIpsText
      .split(/[\n,]/)
      .map(s => s.trim())
      .filter(Boolean);
  };

  const onSubmit = async data => {
    try {
      formRef.current.setErrors({});

      const schema = Yup.object().shape({
        name: Yup.string()
          .min(3, 'Mínimo 3 caracteres')
          .max(120, 'Máximo 120 caracteres')
          .required('O nome é obrigatório'),
        description: Yup.string().max(1000, 'Máximo 1000 caracteres'),
        rate_limit_per_minute: Yup.number()
          .typeError('Deve ser um número')
          .integer('Deve ser inteiro')
          .min(1, 'Mínimo 1')
          .max(10000, 'Máximo 10000'),
      });

      await schema.validate(data, { abortEarly: false });

      if (selectedScopes.length === 0) {
        setScopesError('Selecione pelo menos um escopo.');
        return;
      }

      const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
      const allowed_ips = parseAllowedIps();
      const invalidIps = allowed_ips.filter(ip => !ipRegex.test(ip));
      if (invalidIps.length > 0) {
        formRef.current.setFieldError(
          'allowed_ips_text',
          `IPs inválidos: ${invalidIps.join(', ')}`
        );
        toast.error('Verifique a lista de IPs permitidos.');
        return;
      }

      if (allStores === false && selectedStores.length === 0) {
        toast.error(
          'Selecione pelo menos uma loja ou marque "Todas as lojas".'
        );
        return;
      }

      const payload = {
        name: data.name?.trim(),
        description: data.description?.trim() || null,
        environment,
        scopes: selectedScopes,
        rate_limit_per_minute: data.rate_limit_per_minute
          ? Number(data.rate_limit_per_minute)
          : 60,
        manages_all_stores: allStores,
        allowed_ips,
      };

      if (!allStores) {
        payload.store_ids = selectedStores.map(s => s.value);
      }

      const { data: response } = await api.post(
        'administrator/partner-applications',
        payload
      );

      toast.success('Aplicação criada com sucesso!');

      // O backend retorna { data: { ..., client_secret }, warning }
      setCreatedData(response?.data);
      setRevealOpen(true);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach(e => {
          validationErrors[e.path] = e.message;
        });
        formRef.current.setErrors(validationErrors);
        return;
      }
      if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else if (err?.response?.data?.errors) {
        // mostra primeiro erro de validação do backend
        const firstKey = Object.keys(err.response.data.errors)[0];
        toast.error(err.response.data.errors[firstKey][0]);
      } else {
        toast.error('Não foi possível criar a aplicação.');
      }
    }
  };

  const handleRevealClose = () => {
    setRevealOpen(false);
    // Após copiar/fechar o secret, vai para edição
    if (createdData?.id) {
      history.push(`/aplicacoes-parceiras/${createdData.id}/editar`);
    } else {
      history.push('/aplicacoes-parceiras');
    }
  };

  return (
    <div className="page-content">
      <Container className="container new-layout">
        <PageHeader
          pageTitle="NOVA APLICAÇÃO PARCEIRA"
          backTo="/aplicacoes-parceiras"
          responsivePosition="start"
          desktopPosition="center"
        />

        <Form ref={formRef} onSubmit={onSubmit}>
          <Card className="card new-layout">
            <CardBody className="card-body new-layout px-4 px-lg-5">
              <Row className="w-100 mx-0">
                {/* Coluna esquerda: dados básicos */}
                <Col lg={6} className="px-0 px-lg-4">
                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <FormGroup>
                        <Label>Nome</Label>
                        <Input
                          name="name"
                          type="text"
                          placeholder="Ex: Integração ERP Cliente X"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <FormGroup>
                        <Label>Descrição</Label>
                        <Input
                          name="description"
                          type="textarea"
                          rows={3}
                          placeholder="Para que serve essa integração?"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <FormGroup>
                        <Label>
                          Ambiente
                          <i
                            id="env-tip"
                            className="las la-info-circle font-size-18 ml-1"
                          />
                          <UncontrolledTooltip placement="top" target="env-tip">
                            Após criada, o ambiente não pode ser alterado. Para
                            mudar, crie uma nova aplicação.
                          </UncontrolledTooltip>
                        </Label>
                        <Row className="w-100 mx-0">
                          <Col xs={6} className="px-lg-0">
                            <NavLink
                              className={`
                                new-layout-nav-pill card d-flex flex-column
                                align-items-center h-fit-content w-100
                                ${
                                  environment === 'test'
                                    ? 'checked'
                                    : 'unchecked'
                                }
                                font-size-14
                              `}
                              to="#"
                              onClick={() => setEnvironment('test')}
                            >
                              <span className="text-nowrap">Teste</span>
                            </NavLink>
                          </Col>
                          <Col xs={6}>
                            <NavLink
                              className={`
                                new-layout-nav-pill card d-flex flex-column
                                align-items-center h-fit-content w-100
                                ${
                                  environment === 'live'
                                    ? 'checked'
                                    : 'unchecked'
                                }
                                font-size-14
                              `}
                              to="#"
                              onClick={() => setEnvironment('live')}
                            >
                              <span className="text-nowrap">Produção</span>
                            </NavLink>
                          </Col>
                        </Row>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <FormGroup>
                        <Label>Rate limit (requests por minuto)</Label>
                        <Input
                          name="rate_limit_per_minute"
                          type="number"
                          defaultValue={60}
                          min={1}
                          max={10000}
                          placeholder="60"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <FormGroup>
                        <Label>
                          IPs permitidos (opcional)
                          <i
                            id="ips-tip"
                            className="las la-info-circle font-size-18 ml-1"
                          />
                          <UncontrolledTooltip placement="top" target="ips-tip">
                            Se preenchido, autenticação só é permitida desses
                            IPs. Um por linha (ou separados por vírgula). Em
                            branco = qualquer IP.
                          </UncontrolledTooltip>
                        </Label>
                        <textarea
                          className="form-control"
                          rows={3}
                          name="allowed_ips_text"
                          value={allowedIpsText}
                          onChange={e => setAllowedIpsText(e.target.value)}
                          placeholder={'192.168.1.1\n10.0.0.5'}
                          style={{
                            fontFamily: '"JetBrains Mono", Menlo, monospace',
                            fontSize: '13px',
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>

                {/* Coluna direita: scopes + stores */}
                <Col lg={6} className="px-0 px-lg-4">
                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <Label>Escopos (permissões)</Label>
                      {scopesError && (
                        <p className="text-danger">{scopesError}</p>
                      )}
                      {AVAILABLE_SCOPES.map(scope => (
                        <CustomInput
                          type="checkbox"
                          key={scope.value}
                          id={`scope-${scope.value}`}
                          name={`scope-${scope.value}`}
                          label={scope.label}
                          checked={selectedScopes.includes(scope.value)}
                          className="text-primary mb-2"
                          onChange={() => toggleScope(scope.value)}
                        />
                      ))}
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0 mt-4">
                    <Col xs={6} className="px-0">
                      <Label>Lojas</Label>
                    </Col>
                    <Col xs={6} align="end" className="px-0">
                      <i
                        className={`las la-${
                          showStorePicker ? 'times' : 'plus'
                        } text-muted font-size-18 outline-none ${
                          allStores ? 'd-none' : ''
                        }`}
                        role="button"
                        tabIndex={0}
                        aria-label="add-store"
                        onClick={() => setShowStorePicker(!showStorePicker)}
                      />
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <FormGroup>
                        {!showStorePicker ? (
                          <CustomInput
                            type="checkbox"
                            id="allStores"
                            name="allStores"
                            label="Todas as lojas"
                            checked={allStores}
                            className="text-primary mt-2 mb-2"
                            onChange={() => {
                              setAllStores(!allStores);
                              if (!allStores === true) {
                                setSelectedStores([]);
                              }
                            }}
                          />
                        ) : (
                          <InputSelectAsync
                            loadOptions={loadStoreOptions}
                            name="store_picker"
                            onChange={addStore}
                            placeholder="Buscar loja..."
                            type="text"
                            isClearable
                            loadingMessage={() => 'Buscando lojas...'}
                            onBlur={() => {}}
                          />
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {!allStores && (
                    <Row className="w-100 mx-0">
                      <Col className="px-0">
                        {selectedStores.length > 0 ? (
                          <PerfectScrollbar className="new-layout-managed-stores-scrollbar">
                            {selectedStores.map(store => (
                              <div
                                key={store.value}
                                className="store-ticket my-2"
                              >
                                <Row className="w-100">
                                  <Col xs={11} className="text-truncate pr-0">
                                    {store.label}
                                  </Col>
                                  <Col xs={1} className="px-0" align="end">
                                    <i
                                      className="las la-times text-muted font-size-16 outline-none"
                                      role="button"
                                      tabIndex={0}
                                      aria-label="remove-store"
                                      onClick={() => removeStore(store)}
                                    />
                                  </Col>
                                </Row>
                              </div>
                            ))}
                          </PerfectScrollbar>
                        ) : (
                          <Alert color="danger">
                            Selecione pelo menos uma loja ou marque &quot;Todas
                            as lojas&quot;.
                          </Alert>
                        )}
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Row className="w-100 mx-0 my-4">
            <Col
              align={defineAlignment(windowSize, LG, 'center', 'end')}
              className="px-0"
            >
              <Button
                color="primary"
                className="new-layout-btn main px-4 my-4"
                size="lg"
                type="submit"
              >
                Criar Aplicação
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>

      <SecretRevealModal
        show={revealOpen}
        setShow={setRevealOpen}
        clientId={createdData?.client_id}
        clientSecret={createdData?.client_secret}
        isRotation={false}
        onClose={handleRevealClose}
      />
    </div>
  );
};

export default NewPartnerApplication;
